import { useState, useEffect, forwardRef } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import { NumericFormat } from 'react-number-format';
import debounce from 'lodash/debounce';

import * as Styles from './Input.styles';

const NumericFormatCustom = forwardRef(function NumericFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value?.replace(/,/g, ''),
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
    />
  );
});

const Input = ({
  placeholder,
  name,
  className = '',
  onChange,
  formik,
  endAdornment,
  startAdornment,
  isNumberFormat,
  defaultValue,
  readOnly,
  useDebounce = true,
  forceUpdate = false,
  variant = '',
  onBlur,
  ...rest
}) => {
  const [value, setValue] = useState(formik?.values?.[name] || defaultValue);

  useEffect(() => {
    if (forceUpdate) {
      setValue(defaultValue || formik?.values?.[name]);
    }
  }, [defaultValue, formik?.values?.[name]]);

  if (variant === 'secondary') {
    if (startAdornment) {
      return (
        <Styles.TextField
          {...rest}
          value={formik?.values?.[name] || defaultValue}
          name={name}
          onChange={(event) => {
            if (onChange) {
              onChange(event.target.value);
            }
            if (formik) {
              formik.handleChange(event);
            }
          }}
          onBlur={(event) => {
            if (onBlur) {
              onBlur(event.target.value || defaultValue);
            }
          }}
          placeholder={placeholder}
          className={className}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ),
            inputComponent: isNumberFormat ? NumericFormatCustom : undefined,
          }}
        />
      );
    }

    if (endAdornment) {
      return (
        <Styles.TextField
          {...rest}
          value={formik?.values?.[name] || defaultValue}
          name={name}
          onChange={(event) => {
            if (onChange) {
              onChange(event.target.value);
            }
            if (formik) {
              formik.handleChange(event);
            }
          }}
          onBlur={(event) => {
            if (onBlur) {
              onBlur(event.target.value || defaultValue);
            }
          }}
          placeholder={placeholder}
          className={className}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ),
            inputComponent: isNumberFormat ? NumericFormatCustom : undefined,
          }}
        />
      );
    }

    return (
      <Styles.TextField
        {...rest}
        value={formik?.values?.[name] || defaultValue}
        name={name}
        onChange={(event) => {
          if (onChange) {
            onChange(event.target.value);
          }
          if (formik) {
            formik.handleChange(event);
          }
        }}
        onBlur={(event) => {
          if (onBlur) {
            onBlur(event.target.value || defaultValue);
          }
        }}
        placeholder={placeholder}
        className={className}
        InputProps={{
          inputComponent: isNumberFormat ? NumericFormatCustom : undefined,
          readOnly: readOnly || undefined,
        }}
      />
    );
  }

  const handleInputChange = debounce((event) => {
    setValue(event.target.value);
    if (onChange) {
      onChange(event.target.value);
    }
    if (formik) {
      formik.handleChange(event);
    }
  }, 200);

  const handleInputChangeWithOutDebounce = (event) => {
    setValue(event.target.value);
    if (onChange) {
      onChange(event.target.value);
    }
    if (formik) {
      formik.handleChange(event);
    }
  };

  if (startAdornment) {
    return (
      <Styles.TextField
        {...rest}
        value={value || formik?.values?.[name] || defaultValue}
        name={name}
        onChange={
          useDebounce ? handleInputChange : handleInputChangeWithOutDebounce
        }
        onBlur={(event) => {
          if (onBlur) {
            onBlur(event.target.value);
          }
        }}
        placeholder={placeholder}
        className={className}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ),
          inputComponent: isNumberFormat ? NumericFormatCustom : undefined,
        }}
      />
    );
  }

  if (endAdornment) {
    return (
      <Styles.TextField
        {...rest}
        value={value || formik?.values?.[name] || defaultValue}
        name={name}
        onChange={
          useDebounce ? handleInputChange : handleInputChangeWithOutDebounce
        }
        onBlur={(event) => {
          if (onBlur) {
            onBlur(event.target.value);
          }
        }}
        placeholder={placeholder}
        className={className}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ),
          inputComponent: isNumberFormat ? NumericFormatCustom : undefined,
        }}
      />
    );
  }

  return (
    <Styles.TextField
      {...rest}
      value={value || formik?.values?.[name] || defaultValue}
      name={name}
      onChange={
        useDebounce ? handleInputChange : handleInputChangeWithOutDebounce
      }
      onBlur={(event) => {
        if (onBlur) {
          onBlur(event.target.value);
        }
      }}
      placeholder={placeholder}
      className={className}
      InputProps={{
        inputComponent: isNumberFormat ? NumericFormatCustom : undefined,
        readOnly: readOnly || undefined,
      }}
    />
  );
};

export default Input;
